/* ──────────────────────────────────────────────────────────────────────────
   The sub flow's rules, shared by the server, the pages and the tests. Pure,
   with no imports, because the web half reads it too.

   The bands and their headings are the daily message's own (2026-10-13):
   20 or fewer is Poor, 21 to 80 Average, 81 to 500 Strong, 501 and up
   Popping. A creator's figure is all their pages together: free, VIP and the
   third page.
   ────────────────────────────────────────────────────────────────────────── */

export const PLATFORMS = ["onlyfans", "fansly"] as const;
export type Platform = (typeof PLATFORMS)[number];

export type Pages = { free: number | null; vip: number | null; third: number | null };

export const BAND_IDS = ["poor", "average", "strong", "popping"] as const;
export type BandId = (typeof BAND_IDS)[number];

export const BANDS: { id: BandId; label: string; range: string; heading: string }[] = [
  { id: "poor", label: "Poor", range: "20 or fewer", heading: "POOR NEED URGENT IMPROVEMENT⏬: (BELOW 20 FREE AND VIP SUBS)" },
  { id: "average", label: "Average", range: "21 to 80", heading: "AVERAGE SUB FLOW⚖️: (21-80 FREE AND VIP SUBS)" },
  { id: "strong", label: "Strong", range: "81 to 500", heading: "HIGH SUB FLOW🔝: (81-500 FREE AND VIP SUBS)" },
  { id: "popping", label: "Popping", range: "501 and up", heading: "URGENT SHE’S POPPING🔥: (ABOVE 501 FREE AND VIP SUBS)" },
];

export const total = (pages: Pages) => (pages.free ?? 0) + (pages.vip ?? 0) + (pages.third ?? 0);

/** A day's total, or a longer window's average per reported day. */
export function bandOf(perDay: number): BandId {
  if (perDay <= 20) return "poor";
  if (perDay <= 80) return "average";
  if (perDay <= 500) return "strong";
  return "popping";
}

/** "JACKIE FANSLY", as the message names an account. */
export const messageName = (creator: string, platform: Platform) => (platform === "fansly" ? `${creator} FANSLY` : creator);

const cell = (value: number | null) => `(${value === null ? "--" : value})`;

/**
 * The day in the message's own format, so it can still be pasted into the
 * chat. The third page is only written for a creator who has one.
 */
export function formatMessage(rows: ({ creator: string; platform: Platform } & Pages)[]): string {
  return BANDS.map((band) => {
    const inBand = rows.filter((row) => bandOf(total(row)) === band.id);
    const lines = inBand.length
      ? inBand.map((row) => `● ${messageName(row.creator, row.platform)} ${cell(row.free)} ${cell(row.vip)}${row.third === null ? "" : ` ${cell(row.third)}`}`)
      : ["● NONE"];
    return [band.heading, ...lines].join("\n");
  }).join("\n\n");
}

export type ParsedLine = { creator: string; platform: Platform } & Pages;

/** One day's message: the day it names ("OF STATS UPDATE FOR October 11, 2026"), its lines, and any it couldn't read. */
export type Report = { day: string | null; lines: ParsedLine[]; unreadable: string[] };

const VALUE = /\(\s*(-?\d+|-{1,2}|\u2014|\u2013)?\s*\)/g;
const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const HEADER = /STATS UPDATE FOR\s+([a-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})/i;
// The two people who send it write Fansly differently: "JACKIE FANSLY" and "JACKIE FL".
const FANSLY = / (FANSLY|FL)$/;

function headerDay(line: string): string | null | undefined {
  const m = HEADER.exec(line);
  if (!m) return undefined;
  const month = MONTHS.indexOf(m[1]!.toLowerCase());
  if (month < 0) return null;
  return `${m[3]}-${String(month + 1).padStart(2, "0")}-${m[2]!.padStart(2, "0")}`;
}

/**
 * Reads the daily messages back into numbers. Each "OF STATS UPDATE FOR <day>"
 * starts a report for that day (the numbers are that day's, sent the
 * afternoon after). Then one creator per line, up to three bracketed numbers
 * (free, VIP, third page), "--" for a page with nothing. Headings, blank
 * lines, chat timestamps and "NONE" are skipped; a creator line whose
 * brackets don't read ("(010 (28)") is set aside rather than guessed.
 */
export function parseReports(text: string): Report[] {
  const reports: Report[] = [];
  let current: Report = { day: null, lines: [], unreadable: [] };
  const close = () => {
    if (current.lines.length || current.unreadable.length) reports.push(current);
  };
  for (const raw of text.split(/\r?\n/)) {
    const day = headerDay(raw);
    if (day !== undefined) {
      close();
      current = { day, lines: [], unreadable: [] };
      continue;
    }
    // Bullets differ by sender (● and ▫️), so anything before the name goes.
    const line = raw.replace(/^[^A-Za-z0-9(]+/, "").trim();
    const first = line.indexOf("(");
    if (first <= 0) continue;
    const name = line.slice(0, first).trim().toUpperCase().replace(/\s+/g, " ");
    const tail = line.slice(first);
    // A heading carries its band in brackets too: "(BELOW 20 FREE AND VIP SUBS)" holds letters.
    const rest = tail.replace(VALUE, "");
    if (!name || /[A-Z]/i.test(rest) || /[^A-Z0-9 .'-]/i.test(name)) continue;
    const values = [...tail.matchAll(VALUE)].map((m) => (m[1] && /\d/.test(m[1]) ? Number(m[1]) || 0 : null));
    if (/[\d()]/.test(rest) || !values.length || values.length > 3) {
      current.unreadable.push(line);
      continue;
    }
    const fansly = FANSLY.test(name);
    current.lines.push({
      creator: fansly ? name.replace(FANSLY, "") : name,
      platform: fansly ? "fansly" : "onlyfans",
      free: values[0] ?? null,
      vip: values[1] ?? null,
      third: values[2] ?? null,
    });
  }
  close();
  return reports;
}

/** Every creator line in the text, whatever day it is under. */
export const parseMessage = (text: string): ParsedLine[] => parseReports(text).flatMap((r) => r.lines);

/**
 * The account a message line is about: the same name on the same platform,
 * else the one account whose name starts with the other's ("ERZA" is
 * ERZABEL, "DUFFY" is DUFF), when there is only one.
 */
export function matchAccount<T extends { creator: string; platform: Platform }>(accounts: T[], line: { creator: string; platform: Platform }): T | null {
  const same = accounts.filter((a) => a.platform === line.platform);
  const exact = same.find((a) => a.creator === line.creator);
  if (exact) return exact;
  const near = same.filter((a) => a.creator.startsWith(line.creator) || line.creator.startsWith(a.creator));
  return near.length === 1 ? near[0]! : null;
}

/** Change against an earlier figure: a percentage when it was above zero, else the plain difference. */
export function change(now: number, before: number | null): { kind: "pct" | "diff"; value: number } | null {
  if (before === null) return null;
  if (before > 0) return { kind: "pct", value: Math.round(((now - before) / before) * 100) };
  return { kind: "diff", value: now - before };
}
